import { useEffect, useState } from 'react';
import { Megaphone, Paperclip } from 'lucide-react';
import {
  getAnnouncements,
  type AnnouncementItem,
  type AnnouncementsMeta,
} from '../api/notifications';
import PageHeader from '../components/ui/PageHeader';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { formatDate } from '@/lib/format';

export default function Announcements() {
  const [items, setItems] = useState<AnnouncementItem[]>([]);
  const [meta, setMeta] = useState<AnnouncementsMeta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<string | null>(null);

  async function load(p: number) {
    setLoading(true);
    const res = await getAnnouncements(p);
    if (res.error) {
      setError(res.error);
      if (p === 1) {
        setItems([]);
        setMeta(null);
      }
    } else if (res.data) {
      setError(null);
      setItems(p === 1 ? res.data.data : (prev) => [...prev, ...res.data!.data]);
      setMeta(res.data.meta);
    }
    setLoading(false);
  }

  useEffect(() => { load(1); }, []);

  function handleLoadMore() {
    const next = page + 1;
    setPage(next);
    load(next);
  }

  const hasMore = meta ? meta.current_page < meta.last_page : false;

  return (
    <div className="mx-auto max-w-lg space-y-4 pb-8">
      <PageHeader
        title="Announcements"
        subtitle={meta ? `${meta.total} announcement${meta.total !== 1 ? 's' : ''}` : ''}
      />

      {error ? (
        <div className="rounded-xl border border-warning/25 bg-warning-soft px-4 py-3 text-sm text-warning">
          <p className="font-medium">Announcements could not be loaded.</p>
          <p className="mt-1 text-xs text-warning/80">{error}</p>
        </div>
      ) : null}

      {loading && items.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
          <Megaphone className="h-10 w-10 opacity-20" />
          <p className="text-sm">Loading…</p>
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
          <Megaphone className="h-10 w-10 opacity-20" />
          <p className="text-sm font-medium">No announcements yet</p>
          <p className="text-xs">HR will post updates here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const isExpanded = expanded === item.id;
            return (
              <Card
                key={item.id}
                className="cursor-pointer transition-colors"
                onClick={() => setExpanded(isExpanded ? null : item.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <Megaphone className="h-4 w-4 text-primary" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium leading-snug">{item.title}</p>
                      <p className="mt-1 text-[10px] text-muted-foreground/70">{formatDate(item.sentAt)}</p>

                      {!isExpanded && (
                        <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">
                          {item.body.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()}
                        </p>
                      )}

                      {isExpanded && (
                        <div
                          className="announcement-body prose prose-sm mt-3 max-w-none text-sm text-foreground"
                          dangerouslySetInnerHTML={{ __html: item.body }}
                        />
                      )}

                      {isExpanded && item.attachments.length > 0 && (
                        <div className="mt-3 space-y-1.5" onClick={(e) => e.stopPropagation()}>
                          {item.attachments.map((file) => (
                            <a
                              key={file.url}
                              href={file.url}
                              target="_blank"
                              rel="noreferrer"
                              className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-foreground hover:bg-muted/60"
                            >
                              <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                              <span className="truncate">{file.filename}</span>
                            </a>
                          ))}
                        </div>
                      )}

                      <p className="mt-2 text-xs text-primary">
                        {isExpanded ? '▲ Show less' : '▼ Read more'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {hasMore && (
            <div className="flex justify-center pt-2">
              <Button variant="outline" size="sm" onClick={handleLoadMore} disabled={loading}>
                {loading ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
